'use client';

import { useState } from 'react';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  aspectRatioPresets,
  calculateAspectRatio,
  calculateBreakEvenRoas,
  convertColorFormats,
  convertUnixTimestampInput,
  createCalculatorJson,
  inspectMetaLengths,
  type AspectRatioResult,
  type BreakEvenRoasResult,
  type ColorConversionResult,
  type MetaLengthResult,
  type TimestampConversionResult,
} from '@/lib/browser-calculator-tools';

type Variant =
  | 'break-even-roas-calculator'
  | 'meta-title-meta-description-length-checker'
  | 'aspect-ratio-calculator'
  | 'color-format-converter'
  | 'unix-timestamp-converter';

type ToolResult =
  | {
      kind: 'break-even-roas';
      data: BreakEvenRoasResult;
      jsonText: string;
      plainText: string;
    }
  | {
      kind: 'meta-length';
      data: MetaLengthResult;
      jsonText: string;
      plainText: string;
    }
  | {
      kind: 'aspect-ratio';
      data: AspectRatioResult;
      jsonText: string;
      plainText: string;
    }
  | {
      kind: 'color';
      data: ColorConversionResult;
      jsonText: string;
    }
  | {
      kind: 'timestamp';
      data: TimestampConversionResult;
      jsonText: string;
      plainText: string;
    };

const toolCopy: Record<
  Variant,
  {
    title: string;
    description: string;
    badge: string;
    actionLabel: string;
    successLabel: string;
  }
> = {
  'break-even-roas-calculator': {
    title: 'Calculate the break-even ROAS for one order',
    description:
      'Enter your selling price, costs, fees, and target profit to see how much ad spend one order can support before it stops making sense.',
    badge: 'ROAS',
    actionLabel: 'Calculate ROAS',
    successLabel: 'Break-even ROAS calculation',
  },
  'meta-title-meta-description-length-checker': {
    title: 'Check title and meta description length before publishing',
    description:
      'Paste a title and description, review the character counts, and inspect a lightweight search-style preview locally.',
    badge: 'Meta',
    actionLabel: 'Check lengths',
    successLabel: 'Meta length check',
  },
  'aspect-ratio-calculator': {
    title: 'Calculate reduced aspect ratios and missing dimensions',
    description:
      'Use width, height, and ratio values to reduce dimensions, solve one missing side, and sanity-check common image and video layouts.',
    badge: 'Ratio',
    actionLabel: 'Calculate ratio',
    successLabel: 'Aspect ratio calculation',
  },
  'color-format-converter': {
    title: 'Convert one color between HEX, RGB, RGBA, HSL, and HSLA',
    description:
      'Enter a common web color value, preview the swatch, and copy the format you need for CSS, SVG, or design handoff.',
    badge: 'Color',
    actionLabel: 'Convert color',
    successLabel: 'Color conversion',
  },
  'unix-timestamp-converter': {
    title: 'Convert Unix timestamps into readable date formats',
    description:
      'Enter a Unix timestamp or pick a date and time, then convert between seconds, milliseconds, ISO, UTC, and browser-local time.',
    badge: 'Time',
    actionLabel: 'Convert timestamp',
    successLabel: 'Unix timestamp conversion',
  },
};

function createTextDownload(text: string, fileName: string, mimeType = 'text/plain;charset=utf-8') {
  const url = URL.createObjectURL(new Blob([text], { type: mimeType }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  throw new Error('The browser could not access the clipboard.');
}

function formatAmount(value: number) {
  return Number(value.toFixed(2)).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });
}

function formatPercent(value: number) {
  return `${Number(value.toFixed(1)).toLocaleString(undefined, {
    maximumFractionDigits: 1,
  })}%`;
}

function formatRatioDecimal(value: number) {
  return Number(value.toFixed(4)).toLocaleString(undefined, {
    maximumFractionDigits: 4,
  });
}

function buildBreakEvenText(data: BreakEvenRoasResult) {
  return [
    `Break-even ROAS: ${data.breakEvenRoas.toFixed(2)}x`,
    `Break-even ROAS (%): ${formatPercent(data.breakEvenRoasPercent)}`,
    `Max ad spend per order: ${formatAmount(data.maxAdSpend)}`,
    `Max CAC: ${formatAmount(data.maxCac)}`,
    `Contribution margin before ads: ${formatAmount(data.contributionMargin)}`,
  ].join('\n');
}

function buildMetaText(data: MetaLengthResult) {
  return [
    `Title (${data.titleLength}): ${data.titleStatus.label}`,
    `Description (${data.descriptionLength}): ${data.descriptionStatus.label}`,
    `Preview title: ${data.previewTitle}`,
    `Preview description: ${data.previewDescription}`,
  ].join('\n');
}

function buildAspectText(data: AspectRatioResult) {
  return [
    `Reduced ratio: ${data.reducedRatio}`,
    `Decimal ratio: ${formatRatioDecimal(data.decimalRatio)}`,
    data.solvedWidth != null ? `Solved width: ${data.solvedWidth}` : null,
    data.solvedHeight != null ? `Solved height: ${data.solvedHeight}` : null,
    data.summary,
  ]
    .filter(Boolean)
    .join('\n');
}

function buildTimestampText(data: TimestampConversionResult) {
  return [
    `Unix seconds: ${data.unixSeconds}`,
    `Unix milliseconds: ${data.unixMilliseconds}`,
    `ISO: ${data.isoString}`,
    `UTC: ${data.utcString}`,
    `Local (${data.timeZone}): ${data.localString}`,
  ].join('\n');
}

function getStatusToneClass(tone: MetaLengthResult['titleStatus']['tone']) {
  switch (tone) {
    case 'success':
      return 'status-success';
    case 'danger':
      return 'status-error';
    case 'warning':
    default:
      return '';
  }
}

export default function BrowserCalculatorTool({ variant }: { variant: Variant }) {
  const copy = toolCopy[variant];
  const [salePrice, setSalePrice] = useState('');
  const [productCost, setProductCost] = useState('');
  const [shippingCost, setShippingCost] = useState('');
  const [fees, setFees] = useState('');
  const [targetProfit, setTargetProfit] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [widthValue, setWidthValue] = useState('');
  const [heightValue, setHeightValue] = useState('');
  const [ratioWidthValue, setRatioWidthValue] = useState('');
  const [ratioHeightValue, setRatioHeightValue] = useState('');
  const [colorInput, setColorInput] = useState('#4f46e5');
  const [timestampInput, setTimestampInput] = useState('');
  const [dateInput, setDateInput] = useState('');
  const [result, setResult] = useState<ToolResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  function resetOutput() {
    setResult(null);
    setErrorMessage(null);
    setCreditMessage(null);
    setCopyMessage(null);
  }

  function parseOptionalNumber(value: string) {
    if (!value.trim()) {
      return null;
    }

    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      throw new Error('One of the numeric inputs is invalid.');
    }

    return parsed;
  }

  async function runTool() {
    setIsRunning(true);
    setErrorMessage(null);
    setCreditMessage(null);
    setCopyMessage(null);

    try {
      let nextResult: ToolResult;

      switch (variant) {
        case 'break-even-roas-calculator': {
          const data = calculateBreakEvenRoas({
            salePrice: parseOptionalNumber(salePrice) ?? 0,
            productCost: parseOptionalNumber(productCost) ?? 0,
            shippingCost: parseOptionalNumber(shippingCost) ?? 0,
            fees: parseOptionalNumber(fees) ?? 0,
            targetProfit: parseOptionalNumber(targetProfit) ?? 0,
          });
          nextResult = {
            kind: 'break-even-roas',
            data,
            jsonText: createCalculatorJson(data),
            plainText: buildBreakEvenText(data),
          };
          break;
        }
        case 'meta-title-meta-description-length-checker': {
          const data = inspectMetaLengths(metaTitle, metaDescription);
          nextResult = {
            kind: 'meta-length',
            data,
            jsonText: createCalculatorJson(data),
            plainText: buildMetaText(data),
          };
          break;
        }
        case 'aspect-ratio-calculator': {
          const data = calculateAspectRatio({
            width: parseOptionalNumber(widthValue),
            height: parseOptionalNumber(heightValue),
            ratioWidth: parseOptionalNumber(ratioWidthValue),
            ratioHeight: parseOptionalNumber(ratioHeightValue),
          });
          nextResult = {
            kind: 'aspect-ratio',
            data,
            jsonText: createCalculatorJson(data),
            plainText: buildAspectText(data),
          };
          break;
        }
        case 'color-format-converter': {
          const data = convertColorFormats(colorInput);
          nextResult = {
            kind: 'color',
            data,
            jsonText: createCalculatorJson(data),
          };
          break;
        }
        case 'unix-timestamp-converter': {
          const data = convertUnixTimestampInput(timestampInput, dateInput);
          nextResult = {
            kind: 'timestamp',
            data,
            jsonText: createCalculatorJson(data),
            plainText: buildTimestampText(data),
          };
          break;
        }
      }

      const credits = await consumeToolUsage(
        variant,
        'Credits are not available for another calculator run right now.',
      );
      setResult(nextResult);
      setCreditMessage(formatToolCreditSuccess(copy.successLabel, credits));
    } catch (error) {
      setResult(null);
      setErrorMessage(
        error instanceof Error ? error.message : 'This calculator could not complete the request.',
      );
    } finally {
      setIsRunning(false);
    }
  }

  async function handleCopy(text: string, label: string) {
    try {
      await copyText(text);
      setCopyMessage(label);
      setErrorMessage(null);
    } catch {
      setErrorMessage('The browser could not copy the current output.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{copy.badge}</span>
        <h2 className="tool-live-title">{copy.title}</h2>
        <p className="tool-live-subtitle">{copy.description}</p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            {variant === 'break-even-roas-calculator' ? (
              <>
                <label className="tool-live-field">
                  <span>Selling price</span>
                  <input
                    inputMode="decimal"
                    onChange={(event) => {
                      resetOutput();
                      setSalePrice(event.target.value);
                    }}
                    placeholder="100"
                    type="text"
                    value={salePrice}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Product cost</span>
                  <input
                    inputMode="decimal"
                    onChange={(event) => {
                      resetOutput();
                      setProductCost(event.target.value);
                    }}
                    placeholder="30"
                    type="text"
                    value={productCost}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Shipping / fulfillment</span>
                  <input
                    inputMode="decimal"
                    onChange={(event) => {
                      resetOutput();
                      setShippingCost(event.target.value);
                    }}
                    placeholder="10"
                    type="text"
                    value={shippingCost}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Fees</span>
                  <input
                    inputMode="decimal"
                    onChange={(event) => {
                      resetOutput();
                      setFees(event.target.value);
                    }}
                    placeholder="8"
                    type="text"
                    value={fees}
                  />
                </label>
                <label className="tool-live-field tool-live-field-full">
                  <span>Target profit (optional)</span>
                  <input
                    inputMode="decimal"
                    onChange={(event) => {
                      resetOutput();
                      setTargetProfit(event.target.value);
                    }}
                    placeholder="12"
                    type="text"
                    value={targetProfit}
                  />
                  <small>
                    Use your own store currency values. The calculator stays currency-neutral.
                  </small>
                </label>
              </>
            ) : null}

            {variant === 'meta-title-meta-description-length-checker' ? (
              <>
                <label className="tool-live-field tool-live-field-full">
                  <span>Meta title</span>
                  <input
                    onChange={(event) => {
                      resetOutput();
                      setMetaTitle(event.target.value);
                    }}
                    placeholder="Cleanor practical calculators and converters"
                    type="text"
                    value={metaTitle}
                  />
                </label>
                <label className="tool-live-field tool-live-field-full">
                  <span>Meta description</span>
                  <textarea
                    onChange={(event) => {
                      resetOutput();
                      setMetaDescription(event.target.value);
                    }}
                    placeholder="Check title and meta description length in the browser before publishing."
                    spellCheck
                    value={metaDescription}
                  />
                </label>
              </>
            ) : null}

            {variant === 'aspect-ratio-calculator' ? (
              <>
                <label className="tool-live-field">
                  <span>Width</span>
                  <input
                    inputMode="decimal"
                    onChange={(event) => {
                      resetOutput();
                      setWidthValue(event.target.value);
                    }}
                    placeholder="1920"
                    type="text"
                    value={widthValue}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Height</span>
                  <input
                    inputMode="decimal"
                    onChange={(event) => {
                      resetOutput();
                      setHeightValue(event.target.value);
                    }}
                    placeholder="1080"
                    type="text"
                    value={heightValue}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Ratio width</span>
                  <input
                    inputMode="decimal"
                    onChange={(event) => {
                      resetOutput();
                      setRatioWidthValue(event.target.value);
                    }}
                    placeholder="16"
                    type="text"
                    value={ratioWidthValue}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Ratio height</span>
                  <input
                    inputMode="decimal"
                    onChange={(event) => {
                      resetOutput();
                      setRatioHeightValue(event.target.value);
                    }}
                    placeholder="9"
                    type="text"
                    value={ratioHeightValue}
                  />
                </label>
                <div className="tool-live-field tool-live-field-full">
                  <span>Common presets</span>
                  <div className="tool-live-actions">
                    {aspectRatioPresets.map((preset) => (
                      <button
                        className="button button-secondary"
                        key={preset.label}
                        onClick={() => {
                          resetOutput();
                          setRatioWidthValue(String(preset.width));
                          setRatioHeightValue(String(preset.height));
                        }}
                        type="button"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            ) : null}

            {variant === 'color-format-converter' ? (
              <label className="tool-live-field tool-live-field-full">
                <span>Color input</span>
                <input
                  onChange={(event) => {
                    resetOutput();
                    setColorInput(event.target.value);
                  }}
                  placeholder="#4f46e5 or rgb(79, 70, 229)"
                  spellCheck={false}
                  type="text"
                  value={colorInput}
                />
                <small>
                  Examples: `#4f46e5`, `rgb(79, 70, 229)`, `rgba(79,70,229,0.72)`, `hsl(243, 76%,
                  59%)`
                </small>
              </label>
            ) : null}

            {variant === 'unix-timestamp-converter' ? (
              <>
                <label className="tool-live-field tool-live-field-full">
                  <span>Unix timestamp or ISO date</span>
                  <input
                    onChange={(event) => {
                      resetOutput();
                      setTimestampInput(event.target.value);
                    }}
                    placeholder="1711718400 or 2026-03-29T12:00:00Z"
                    spellCheck={false}
                    type="text"
                    value={timestampInput}
                  />
                </label>
                <label className="tool-live-field tool-live-field-full">
                  <span>Date and time</span>
                  <input
                    onChange={(event) => {
                      resetOutput();
                      setDateInput(event.target.value);
                    }}
                    type="datetime-local"
                    value={dateInput}
                  />
                  <small>
                    Leave the timestamp field empty if you want to convert from a picked local date.
                  </small>
                </label>
              </>
            ) : null}
          </div>

          <div className="tool-live-actions">
            <button
              className="button"
              disabled={isRunning}
              onClick={() => void runTool()}
              type="button"
            >
              {isRunning ? 'Working…' : copy.actionLabel}
            </button>
          </div>

          {errorMessage ? <p className="status-message status-error">{errorMessage}</p> : null}
          {creditMessage ? <p className="status-message status-success">{creditMessage}</p> : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <aside className="tool-live-panel">
          {result?.kind === 'break-even-roas' ? (
            <>
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => void handleCopy(result.plainText, 'ROAS summary copied.')}
                  type="button"
                >
                  Copy summary
                </button>
                <button
                  className="button button-secondary"
                  onClick={() =>
                    createTextDownload(
                      result.jsonText,
                      'break-even-roas.json',
                      'application/json;charset=utf-8',
                    )
                  }
                  type="button"
                >
                  Download JSON
                </button>
              </div>
              <div className="tool-live-stats">
                <div>
                  <strong>Break-even ROAS</strong>
                  <span>{result.data.breakEvenRoas.toFixed(2)}x</span>
                </div>
                <div>
                  <strong>Break-even ROAS (%)</strong>
                  <span>{formatPercent(result.data.breakEvenRoasPercent)}</span>
                </div>
                <div>
                  <strong>Max ad spend / order</strong>
                  <span>{formatAmount(result.data.maxAdSpend)}</span>
                </div>
                <div>
                  <strong>Contribution margin</strong>
                  <span>{formatAmount(result.data.contributionMargin)}</span>
                </div>
              </div>
              <div className="tool-live-panel-note">
                <strong>What this means</strong>
                <span>
                  Your ads need to return at least {result.data.breakEvenRoas.toFixed(2)}x revenue
                  to break even.
                </span>
              </div>
            </>
          ) : null}

          {result?.kind === 'meta-length' ? (
            <>
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => void handleCopy(result.plainText, 'Meta summary copied.')}
                  type="button"
                >
                  Copy summary
                </button>
                <button
                  className="button button-secondary"
                  onClick={() =>
                    createTextDownload(
                      result.jsonText,
                      'meta-lengths.json',
                      'application/json;charset=utf-8',
                    )
                  }
                  type="button"
                >
                  Download JSON
                </button>
              </div>
              <article className="tool-html-search-preview">
                <strong>{result.data.previewTitle}</strong>
                <span>{result.data.previewUrl}</span>
                <p>{result.data.previewDescription}</p>
              </article>
              <div className="tool-html-meta-grid">
                <div className="tool-live-panel-note">
                  <strong>Title length</strong>
                  <span>
                    {result.data.titleLength} characters ·{' '}
                    <span className={getStatusToneClass(result.data.titleStatus.tone)}>
                      {result.data.titleStatus.label}
                    </span>
                  </span>
                </div>
                <div className="tool-live-panel-note">
                  <strong>Description length</strong>
                  <span>
                    {result.data.descriptionLength} characters ·{' '}
                    <span className={getStatusToneClass(result.data.descriptionStatus.tone)}>
                      {result.data.descriptionStatus.label}
                    </span>
                  </span>
                </div>
              </div>
            </>
          ) : null}

          {result?.kind === 'aspect-ratio' ? (
            <>
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => void handleCopy(result.plainText, 'Aspect ratio summary copied.')}
                  type="button"
                >
                  Copy summary
                </button>
                <button
                  className="button button-secondary"
                  onClick={() =>
                    createTextDownload(
                      result.jsonText,
                      'aspect-ratio.json',
                      'application/json;charset=utf-8',
                    )
                  }
                  type="button"
                >
                  Download JSON
                </button>
              </div>
              <div className="tool-live-stats">
                <div>
                  <strong>Reduced ratio</strong>
                  <span>{result.data.reducedRatio}</span>
                </div>
                <div>
                  <strong>Decimal ratio</strong>
                  <span>{formatRatioDecimal(result.data.decimalRatio)}</span>
                </div>
                {result.data.solvedWidth != null ? (
                  <div>
                    <strong>Solved width</strong>
                    <span>{result.data.solvedWidth}</span>
                  </div>
                ) : null}
                {result.data.solvedHeight != null ? (
                  <div>
                    <strong>Solved height</strong>
                    <span>{result.data.solvedHeight}</span>
                  </div>
                ) : null}
              </div>
              <div className="tool-live-panel-note">
                <strong>Summary</strong>
                <span>{result.data.summary}</span>
              </div>
            </>
          ) : null}

          {result?.kind === 'color' ? (
            <>
              <div className="tool-live-preview-frame" style={{ padding: 24, minHeight: 176 }}>
                <div
                  aria-label="Converted color preview"
                  style={{
                    width: '100%',
                    height: 128,
                    borderRadius: 18,
                    background: result.data.swatch,
                    border: '1px solid rgba(148, 163, 184, 0.35)',
                  }}
                />
              </div>
              <div className="tool-html-meta-grid">
                {[
                  ['HEX', result.data.hex],
                  ['RGB', result.data.rgb],
                  ['RGBA', result.data.rgba],
                  ['HSL', result.data.hsl],
                  ['HSLA', result.data.hsla],
                ].map(([label, value]) => (
                  <div className="tool-live-panel-note" key={label}>
                    <strong>{label}</strong>
                    <span>{value}</span>
                    <button
                      className="button button-secondary"
                      onClick={() => void handleCopy(value, `${label} copied.`)}
                      type="button"
                    >
                      Copy
                    </button>
                  </div>
                ))}
              </div>
            </>
          ) : null}

          {result?.kind === 'timestamp' ? (
            <>
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => void handleCopy(result.plainText, 'Timestamp summary copied.')}
                  type="button"
                >
                  Copy summary
                </button>
                <button
                  className="button button-secondary"
                  onClick={() =>
                    createTextDownload(
                      result.jsonText,
                      'timestamp-conversion.json',
                      'application/json;charset=utf-8',
                    )
                  }
                  type="button"
                >
                  Download JSON
                </button>
              </div>
              <div className="tool-html-meta-grid">
                <div className="tool-live-panel-note">
                  <strong>Unix seconds</strong>
                  <span>{result.data.unixSeconds}</span>
                </div>
                <div className="tool-live-panel-note">
                  <strong>Unix milliseconds</strong>
                  <span>{result.data.unixMilliseconds}</span>
                </div>
                <div className="tool-live-panel-note">
                  <strong>ISO</strong>
                  <span>{result.data.isoString}</span>
                </div>
                <div className="tool-live-panel-note">
                  <strong>UTC</strong>
                  <span>{result.data.utcString}</span>
                </div>
                <div className="tool-live-panel-note">
                  <strong>Local time</strong>
                  <span>{result.data.localString}</span>
                </div>
                <div className="tool-live-panel-note">
                  <strong>Time zone</strong>
                  <span>{result.data.timeZone}</span>
                </div>
              </div>
            </>
          ) : null}

          {!result ? (
            <div className="tool-live-preview">
              <h3>What you get</h3>
              <p>
                {variant === 'break-even-roas-calculator'
                  ? 'Break-even ROAS, max ad spend per order, and contribution margin.'
                  : null}
                {variant === 'meta-title-meta-description-length-checker'
                  ? 'Title and description counts with a lightweight search-style preview.'
                  : null}
                {variant === 'aspect-ratio-calculator'
                  ? 'A reduced aspect ratio plus a solved missing dimension when enough inputs are present.'
                  : null}
                {variant === 'color-format-converter'
                  ? 'Converted HEX, RGB, RGBA, HSL, and HSLA values with a live swatch.'
                  : null}
                {variant === 'unix-timestamp-converter'
                  ? 'Unix seconds, Unix milliseconds, ISO, UTC, and browser-local time.'
                  : null}
              </p>
            </div>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
